/**
 * @file components/CTA/component.js
 * @copyright 2021 CBC/Radio-Canada. All rights reserved.
 *
 */

import { Fragment } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import LinkWrap from "../LinkWrap/component";
import { Heading } from "../Heading/component";
import IconChevron from "../Icon/IconChevron";
import { getImage } from "./utils";
import featureAttr from "../../utils/featureAttr";
import { ACCOUNT_VARIANT, BUTTON_VARIANT, LINK_VARIANT } from "./constants";
import AccountCTA from "./components/AccountCTA/component";
import LinkCallToAction from "../../stories/Components/CTA";
/**
 * CTA
 * @component
 * Renders a call to action as either sub section heading or as a section heading.
 *
 */

const CTA = (props) => {
    const {
        external = false,
        className,
        extraContent = null,
        titleVisible = true,
        variant = LINK_VARIANT,
    } = props;

    const CTAProps = {
        ...props,
        external,
        className,
        extraContent,
        titleVisible,
        variant,
    };
    const pagePath = useSelector((state) => state.app.path);

    switch (variant) {
        case BUTTON_VARIANT:
            return <LegacyButtonCTA {...CTAProps} />;
        case LINK_VARIANT:
            // Homepage CTAs (eg. redesigned RCI widget & Contest widget) use Storybook LInkCTA component
            if (pagePath === "homepage") {
                return (
                    <LinkCallToAction
                        {...CTAProps}
                        imageUrl={CTAProps?.image?.url}
                    />
                );
            }
            return <LinkCTA {...CTAProps} />;
        case ACCOUNT_VARIANT:
            return <AccountCTA />;
        default:
            return null;
    }
};

const LegacyButtonCTA = ({
    text,
    linkUrl,
    linkText,
    external,
    description,
    descriptionClassName,
    className,
    extraContent,
    flexPage,
    image,
    alt,
    titleVisible,
}) => {
    const classes = classNames(
        "CTA buttonCTA sclt-storyCTA",
        flexPage ? "flexPage" : "standardPage",
        image ? "imageCTA" : null,
        className
    );

    return (
        <div
            className={classes}
            {...featureAttr({ name: "link" })}
            data-testid="legacy-button-cta"
        >
            {getImage(image, alt)}
            <div className="CTADescription">
                <Heading
                    level="h2"
                    levelClassName="h3"
                    text={text}
                    className={!titleVisible ? "a11y" : undefined}
                />
                <p className={descriptionClassName}>{description}</p>
            </div>
            <div className="CTALink">
                <LinkWrap url={linkUrl} external={external}>
                    {linkText}
                    <IconChevron
                        isDecorative={true}
                        className="white horizontal"
                    />
                </LinkWrap>
            </div>
            {extraContent}
        </div>
    );
};

const LinkCTA = ({
    text,
    linkUrl,
    linkText,
    external,
    description,
    className,
    extraContent,
    flexPage,
    image,
    alt,
    titleVisible,
}) => {
    const classes = classNames(
        "CTA linkCTA sclt-linkCTA",
        flexPage ? "flexPage" : "standardPage",
        image ? "imageCTA" : null,
        className
    );

    const linkDescription = (
        <Fragment>
            <p className="heading-description">
                {description}&nbsp;&nbsp;
                <LinkWrap
                    url={linkUrl}
                    external={external}
                    className="heading-link CTA-heading-link"
                    data-feature-name="link"
                    data-cy="cta-heading-link"
                >
                    {linkText}
                    <IconChevron
                        width={"10px"}
                        height={"10px"}
                        className={classNames("horizontal", "headingIcon")}
                    />
                </LinkWrap>
            </p>
        </Fragment>
    );

    return (
        <div
            className={classes}
            {...featureAttr({ name: "link" })}
            data-testid="link-cta"
        >
            {getImage(image, alt, { sizesRatio: "100" })}
            <Heading
                className={!titleVisible ? "a11y" : undefined}
                level="h2"
                levelClassName={"h3"}
                text={text}
                main={linkDescription}
            />
            {extraContent}
        </div>
    );
};

CTA.propTypes = {
    text: PropTypes.string, //Text for heading element
    description: PropTypes.string, // Description text
    linkUrl: PropTypes.string, // CTA Link URL
    linkText: PropTypes.string, // CTA Link text
    external: PropTypes.bool, //True when the link is external
    className: PropTypes.string, //Additional class names added to the heading container
    extraContent: PropTypes.element,
    image: PropTypes.object,
    titleVisible: PropTypes.bool,
    descriptionClassName: PropTypes.string, //used mostly for testing purposes
    variant: PropTypes.oneOf([BUTTON_VARIANT, LINK_VARIANT, ACCOUNT_VARIANT]),
};

export default CTA;
